import React, { useEffect, useRef, useState } from 'react';
import { Camera } from 'lucide-react';
import { cn } from '@/lib/utils';

const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png,image/webp';

/** Icon-only profile picker — no labels or helper text. */
export default function ProfilePhotoBadge({
  value,
  onChange,
  name = '',
  size = 72,
  className,
}) {
  const inputRef = useRef(null);
  const [imageFailed, setImageFailed] = useState(false);
  const initial = (name || 'U').trim().charAt(0).toUpperCase();
  const px = typeof size === 'number' ? size : 72;

  useEffect(() => {
    setImageFailed(false);
  }, [value]);

  const pickFile = (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      onChange?.(value, { error: 'Photo must be under 2 MB' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange?.(reader.result, { error: null, file });
    reader.readAsDataURL(file);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={cn(
          'group relative mx-auto block overflow-hidden rounded-full border-2 border-[#C9A84C]/40',
          'bg-gradient-to-br from-[#1a3050] to-[#0a1628] shadow-[0_8px_28px_rgba(0,0,0,0.35)]',
          'transition-transform active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/45',
          className,
        )}
        style={{ width: px, height: px }}
        aria-label="Profile photo"
      >
        {value && !imageFailed ? (
          <img
            src={value}
            alt=""
            className="h-full w-full object-cover"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <span
            className="flex h-full w-full items-center justify-center font-bold text-[#E8D5A3]"
            style={{ fontSize: Math.round(px * 0.36) }}
          >
            {initial}
          </span>
        )}
        <span
          className="absolute bottom-0.5 right-0.5 flex h-7 w-7 items-center justify-center rounded-full border-2 border-[#0a1628] bg-[#C9A84C] text-[#0a1628]"
        >
          <Camera className="h-3.5 w-3.5" strokeWidth={2.25} />
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          pickFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </>
  );
}
